import React, { useState } from 'react'
import { Table, Tabs } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import {
    getNoPointGoods,
    getPointsDeductionList,
    getPointsDeductionRecords,
    getPointsTotalList,
    getRealtimePointsList,
} from '../api'
import SettingPage, { SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'

type PointsTab = 'noPointGoods' | 'realtime' | 'deductionRank' | 'total' | 'deductionRecords'

const getMemberName = (member: any) => member?.name || member?.user?.nickname || '--'

const PointsOperations: React.FC = () => {
    const [tab, setTab] = useState<PointsTab>('noPointGoods')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const req = useRequest(
        async () => {
            const params = { page, limit: pageSize }
            if (tab === 'noPointGoods') return getNoPointGoods(params)
            if (tab === 'realtime') return getRealtimePointsList(params)
            if (tab === 'deductionRank') return getPointsDeductionList(params)
            if (tab === 'total') return getPointsTotalList(params)

            return getPointsDeductionRecords(params)
        },
        {
            refreshDeps: [tab, page, pageSize],
            onError: e => $msg('error', e.message || '获取积分业务数据失败'),
        },
    )
    const goodsColumns: ColumnsType<any> = [
        { title: '商品名称', dataIndex: 'name', render: value => value || '--' },
        { title: '一级分类', dataIndex: ['group', 'name'], width: 180, render: value => value || '--' },
        { title: '二级分类', dataIndex: ['secondary_group', 'name'], width: 180, render: value => value || '--' },
        { title: '积分状态', width: 120, render: () => '不参与积分' },
    ]
    const memberColumns: ColumnsType<any> = [
        {
            title: '排名',
            key: 'rank',
            width: 80,
            render: (_value, _record, index) => (page - 1) * pageSize + index + 1,
        },
        { title: '会员', render: (_value, record) => getMemberName(record.member || record) },
        { title: '手机号', width: 150, render: (_value, record) => (record.member || record)?.phone || '--' },
        {
            title: tab === 'realtime' ? '当前积分' : tab === 'total' ? '累计获得积分' : '累计抵扣积分',
            width: 150,
            render: (_value, record) =>
                tab === 'realtime'
                    ? record.points || 0
                    : tab === 'total'
                    ? record.points_total || 0
                    : record.points_deduction_total || 0,
        },
        ...(tab === 'deductionRank'
            ? [
                  {
                      title: '累计抵现金额',
                      dataIndex: 'amount_deducted_total',
                      width: 150,
                      render: (value: any) => `${value || 0} 元`,
                  },
              ]
            : []),
    ]
    const recordColumns: ColumnsType<any> = [
        {
            title: '抵扣时间',
            dataIndex: 'create_time',
            width: 170,
            render: value => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
        },
        { title: '订单号', dataIndex: 'order_no', width: 220, render: value => value || '--' },
        { title: '抵扣积分', dataIndex: 'deduction_points', width: 130, render: value => value || 0 },
        { title: '抵现金额', dataIndex: 'deduction_amount', width: 130, render: value => `${value || 0} 元` },
        { title: '订单实付', dataIndex: 'order_actual_payment', width: 130, render: value => `${value || 0} 元` },
    ]
    const dataSource =
        tab === 'noPointGoods'
            ? req.data?.goods_list || []
            : tab === 'realtime'
            ? req.data?.member_list || []
            : tab === 'deductionRank'
            ? req.data?.points_deduction || []
            : tab === 'total'
            ? req.data?.point_total_response || []
            : req.data?.points_deduction_record_list || []
    const columns = tab === 'noPointGoods' ? goodsColumns : tab === 'deductionRecords' ? recordColumns : memberColumns

    return (
        <SettingPage
            title="积分业务"
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            extra={
                <Button types="primary" loading={req.loading} onClick={() => req.refresh()}>
                    刷新
                </Button>
            }
        >
            <Tabs
                activeKey={tab}
                onChange={value => {
                    setTab(value as PointsTab)
                    setPage(1)
                }}
            >
                <Tabs.TabPane tab="不参与积分商品" key="noPointGoods" />
                <Tabs.TabPane tab="实时积分榜" key="realtime" />
                <Tabs.TabPane tab="积分抵现榜" key="deductionRank" />
                <Tabs.TabPane tab="累计积分榜" key="total" />
                <Tabs.TabPane tab="积分抵现记录" key="deductionRecords" />
            </Tabs>
            {tab === 'noPointGoods' ? (
                <div className="member-config-page__notice">
                    商品是否参与积分请在商品资料中修改；这里集中展示当前已设为不参与积分的商品。
                </div>
            ) : null}
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey={(record, index) => record.id || record.order_no || record.member?.id || index}
                    loading={req.loading}
                    columns={columns}
                    dataSource={dataSource}
                    pagination={getMemberTablePagination({
                        current: page,
                        pageSize,
                        total: getTableTotal(req.data?.pagination),
                        onChange: setPage,
                        onShowSizeChange: (nextPage, nextSize) => {
                            setPage(nextPage)
                            setPageSize(nextSize)
                        },
                    })}
                    scroll={{ x: 'max-content' }}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default PointsOperations
